import { useMemo, useState } from 'react';
import { NavIcon } from '../../../shell/shared';
import type { GuidedNotePreset, TabConfig } from './useEncounterTabConfig';
import { resolveGuidedNoteCards } from './guided-note-presets';

// ─── Types ───

interface Props {
  preset: GuidedNotePreset;
  visibleTabs: TabConfig[];
  primary: string;
  renderSubmodule: (key: string) => React.ReactNode;
  renderHeroSection?: (cardId: string) => React.ReactNode | undefined;
}

// ─── Component ───

export function GuidedNoteRenderer({
  preset,
  visibleTabs,
  primary,
  renderSubmodule,
  renderHeroSection,
}: Props) {
  const visibleKeys = useMemo(() => new Set(visibleTabs.map((t) => t.key)), [visibleTabs]);

  const sections = useMemo(
    () => resolveGuidedNoteCards(preset, visibleKeys),
    [preset, visibleKeys],
  );

  const [focusedId, setFocusedId] = useState<string | null>(null);

  if (sections.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--sp-8)',
          textAlign: 'center',
          color: 'var(--text-muted)',
          fontSize: 'var(--font-size-base)',
        }}
      >
        No sections available for this preset. Enable submodules in settings.
      </div>
    );
  }

  const sectionsToRender = focusedId ? sections.filter((s) => s.id === focusedId) : sections;

  return (
    <div
      style={{
        padding: '0 var(--sp-6) var(--sp-6)',
        display: 'flex',
        flexDirection: 'column',
        flex: focusedId ? 1 : undefined,
        minHeight: focusedId ? 0 : undefined,
      }}
    >
      {/* Focus mode: collapse bar */}
      {focusedId && (
        <button
          onClick={() => setFocusedId(null)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 4px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: primary,
            fontSize: 'var(--font-size-xs)',
            fontWeight: 500,
          }}
        >
          <NavIcon name="chevron-left" />
          Back to Guided Note
        </button>
      )}

      {sectionsToRender.map((section) => {
        const activeKeys = section.submoduleKeys.filter((k) => visibleKeys.has(k));
        const heroContent = section.hero ? renderHeroSection?.(section.id) : undefined;
        const isFocused = focusedId === section.id;

        return (
          <div
            key={section.id}
            style={{
              borderBottom: isFocused ? 'none' : '1px solid var(--border)',
              flex: isFocused ? 1 : undefined,
              display: isFocused ? 'flex' : undefined,
              flexDirection: isFocused ? 'column' : undefined,
              minHeight: isFocused ? 0 : undefined,
            }}
          >
            {/* ── Section header ── */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '14px 4px 8px',
              }}
            >
              <span style={{ color: primary, display: 'flex', flexShrink: 0 }}>
                <NavIcon name={section.icon} />
              </span>
              <span
                style={{
                  flex: 1,
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--text)',
                }}
              >
                {section.title}
              </span>
              {/* Expand / Collapse — hero sections only */}
              {section.hero && (
                <button
                  onClick={() => setFocusedId(isFocused ? null : section.id)}
                  title={isFocused ? 'Collapse' : 'Expand'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    background: isFocused ? primary : 'var(--bg-surface)',
                    color: isFocused ? '#fff' : 'var(--text-muted)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 500,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  <NavIcon name={isFocused ? 'list' : 'layout-grid'} />
                  {isFocused ? 'Collapse' : 'Expand'}
                </button>
              )}
            </div>

            {/* ── Section body ── */}
            <div
              style={{
                ...(section.emphasized
                  ? {
                      background: 'var(--bg-surface-2)',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: isFocused ? 0 : 14,
                    }
                  : { marginBottom: isFocused ? 0 : 4 }),
                ...(isFocused ? { flex: 1, minHeight: 0, overflow: 'auto' } : {}),
              }}
            >
              {heroContent ??
                (section.columns ? (
                  // PRD.314 — responsive grid: two active submodules sit side
                  // by side on desktop, one takes the full width, and narrow
                  // screens stack. `auto-fit` means a third history would wrap
                  // rather than assuming a permanent maximum of two.
                  <div className="pc-guided-columns">
                    {activeKeys.map((key) => (
                      <div
                        key={key}
                        style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}
                      >
                        {renderSubmodule(key)}
                      </div>
                    ))}
                  </div>
                ) : (
                  activeKeys.map((key, idx) => (
                    <div
                      key={key}
                      style={idx > 0 ? { borderTop: '1px dashed var(--border)' } : undefined}
                    >
                      {renderSubmodule(key)}
                    </div>
                  ))
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
